import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Plus, Trash2, SquarePen } from 'lucide-react';
import { getAllAddons, createAddon, updateAddon, deleteAddon } from '../../../Services/AddRetreatManagement/RetreatDetailsApi';
import DeleteModal from '../../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../../Components/Common/GlobalSanckbar';


const Addons = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [addons, setAddons] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        displayOrder: '1',
        isActive: true
    });
    const [currentItem, setCurrentItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const fetchAddons = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllAddons();
            if (response && response.success) {
                setAddons(response.data);
            } else {
                setError("Failed to load addons.");
            }
        } catch (err) {
            setError("Failed to fetch addons. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAddons();
    }, [fetchAddons]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEdit(true);
            setCurrentItem(item);
            setFormData({
                name: item.name,
                displayOrder: item.displayOrder !== undefined && item.displayOrder !== null && item.displayOrder !== '' ? String(item.displayOrder) : '1',
                isActive: item.isActive !== undefined ? item.isActive : true
            });
        } else {
            setIsEdit(false);
            setCurrentItem(null);
            setFormData({
                name: '',
                displayOrder: '1',
                isActive: true
            });
        }
        setValidationErrors({});
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentItem(null);
        setFormData({ name: '', displayOrder: '1', isActive: true });
        setValidationErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        if (name === 'displayOrder') {
            if (value !== "" && (!/^\d+$/.test(value) || Number(value) > 999)) {
                return;
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setValidationErrors({ name: 'Name is required' });
            return;
        }

        if (formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined && (Number(formData.displayOrder) > 999 || Number(formData.displayOrder) < 1)) {
            setValidationErrors({ displayOrder: "Display order must be between 1 and 999." });
            return;
        }

        const payload = {
            name: formData.name,
            isActive: formData.isActive,
            ...(formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined ? { displayOrder: Number(formData.displayOrder) } : {})
        };

        try {
            setSubmitting(true);
            setValidationErrors({});
            let response;
            if (isEdit) {
                response = await updateAddon(currentItem.id, payload);
            } else {
                response = await createAddon(payload);
            }
            if (response && response.success) {
                fetchAddons();
                handleCloseModal();
                showSnackbar(`Addon ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
            } else {
                showSnackbar('Failed to save addon', 'error');
            }
        } catch (err) {
            const serverError = err.response?.data;
            const errorMessage = Array.isArray(serverError?.errors)
                ? serverError.errors.join(", ") : (serverError?.message || err.message || 'Error saving addon');
            if (serverError && serverError.details) {
                const fieldErrors = {};
                serverError.details.forEach(detail => {
                    const fieldName = detail.field === 'general' ? 'name' : detail.field;
                    fieldErrors[fieldName] = detail.message;
                });
                setValidationErrors(fieldErrors);
            } else {
                setValidationErrors({ name: errorMessage });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsDeleting(true);
            setDeleteError(null);
            const response = await deleteAddon(selectedId);
            if (response && response.success) {
                fetchAddons();
                setDeleteModalOpen(false);
                setSelectedId(null);
                showSnackbar('Addon deleted successfully!', 'success');
            } else {
                const errorMsg = Array.isArray(response?.errors)
                    ? response.errors.join(", ")
                    : (response?.message || 'Failed to delete addon');
                setDeleteError(errorMsg);
            }
        } catch (err) {
            console.error("Delete Error:", err);
            const serverError = err.response?.data;
            const errorMessage = Array.isArray(serverError?.errors)
                ? serverError.errors.join(", ")
                : (serverError?.message || err.message || 'Error deleting addon');
            setDeleteError(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Add-ons Management
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            variant="contained" startIcon={<Plus size={18} />} onClick={() => handleOpenModal()}
                            sx={{
                                bgcolor: '#FFBD65',
                                px: 2.5,
                                color: '#fff',
                                borderRadius: '6px',
                                height: '40px',
                                textTransform: 'none',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Addon
                        </Button>
                    </PermissionWrapper>
                </Stack>

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 950 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Display Order</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {error ? (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                            <Typography color="error" sx={{ fontSize: '0.85rem' }}>
                                                {error}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : addons.length > 0 ? (
                                    addons.map((addon, index) => (
                                        <TableRow key={addon.id} sx={{ transition: 'background-color 0.2s', '& td': { borderBottom: '1px solid #F0F0F0' }, '&:last-child td': { borderBottom: 0 } }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{index + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>{addon.name}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666' }}>{addon.displayOrder !== undefined && addon.displayOrder !== null ? addon.displayOrder : 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.5, py: 0.3,
                                                    borderRadius: '4px',
                                                    bgcolor: addon.isActive ? '#E8F5E9' : '#FEECEB',
                                                    color: addon.isActive ? '#2E7D32' : '#F44336',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}>
                                                    {addon.isActive ? "Active" : "Inactive"}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {addon.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {addon.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(addon.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(addon.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple onClick={() => handleOpenModal(addon)} sx={{ color: '#FFBD65' }} size="small">
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                onClick={() => handleDeleteClick(addon.id)} sx={{ color: '#FF4D49' }} size="small">
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No addons found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Create/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Addon' : 'Add New Addon'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            <TextField fullWidth label="Name" name="name" size='small' value={formData.name}
                                onChange={handleInputChange} required
                                error={!!validationErrors.name} helperText={validationErrors.name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label="Display Order"
                                name="displayOrder"
                                value={formData.displayOrder}
                                onChange={handleInputChange}
                                placeholder="Enter display order (1 - 999)"
                                inputProps={{ min: 1, max: 999, onWheel: (e) => e.target.blur() }}
                                error={Boolean(validationErrors.displayOrder)}
                                helperText={validationErrors.displayOrder}
                                size="small"
                                sx={{
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                        }}
                                    />
                                }
                                label={<span style={{ fontSize: '0.85rem' }}>Active Status</span>}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button onClick={handleCloseModal} disabled={submitting} disableElevation disableFocusRipple disableRipple disableTouchRipple sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>
                            Cancel
                        </Button>
                        <Button variant="contained" disabled={submitting}
                            onClick={handleSubmit} disableElevation disableFocusRipple disableRipple disableTouchRipple
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#fff',
                                textTransform: 'none',
                                borderRadius: '6px',
                                px: 3,
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Create')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleDeleteConfirm} title="Delete Addon" error={deleteError} loading={isDeleting}
                    description="Are you sure you want to delete this addon? This action cannot be undone."
                />
            </Box>
        </PermissionWrapper>
    );
};

export default Addons;

